import React, { useEffect, useState } from 'react'
import { Spin, Alert } from 'antd'
import { useToken } from '../hooks'
import { View } from '../../styles/reactnative'

interface TokenExchangeGuardProps {
	children: React.ReactNode
}

/**
 * TokenExchangeGuard
 *
 * ssotoken으로 토큰 교체 시 모든 컴포넌트 렌더링을 막고
 * 토큰 교체가 완료될 때까지 로딩 상태를 표시합니다.
 *
 * 이를 통해 Header 등 다른 컴포넌트들이 이전 토큰으로
 * API를 호출하는 Race Condition을 방지합니다.
 */
const TokenExchangeGuard: React.FC<TokenExchangeGuardProps> = ({ children }) => {
	const [exchanging, setExchanging] = useState(false)
	const { data: token, mutate } = useToken()

	useEffect(() => {
		// 이미 교체 중이면 중복 실행 방지
		if (exchanging) {
			console.log('[TokenExchangeGuard] 이미 교체 중 - 스킵')
			return
		}

		const params = new URLSearchParams(window.location.search)
		const ssotoken = params.get('ssotoken')

		// ssotoken이 있고, 유효한 값일 때 처리
		if (ssotoken && ssotoken !== '0' && ssotoken !== 'null') {
			// ⚠️ 중요: 기존 토큰이 있고, 새 토큰과 다를 때만 교체!
			// 토큰이 없으면 LoginPage에서 처리 (SecureOn 검증 필요)

			if (token && token !== ssotoken) {
				// 토큰 교체 시나리오
				console.log(`[TokenExchangeGuard] 토큰 교체 시작:`, {
					oldToken: token.substring(0, 20) + '...',
					newToken: ssotoken.substring(0, 20) + '...'
				})

				setExchanging(true)

				// 토큰 교체 실행
				mutate(ssotoken)
					.then(() => {
						console.log(`[TokenExchangeGuard] 토큰 교체 완료`)

						// /partner로 리다이렉트
						setTimeout(() => {
							window.location.href = '/partner'
						}, 100)
					})
					.catch(err => {
						console.error(`[TokenExchangeGuard] 토큰 교체 실패:`, err)
						setExchanging(false)
					})
			}
			else if (!token) {
				// 토큰이 없으면 LoginPage로 넘김 (기존 로그인 프로세스)
				console.log('[TokenExchangeGuard] 토큰 없음 - LoginPage에서 처리')
			}
			else {
				// 같은 토큰이면 무시
				console.log('[TokenExchangeGuard] 동일한 토큰 - 무시')
			}
		}
	}, [token, mutate, exchanging])

	// 토큰 교체 중에는 로딩 스피너만 표시
	if (exchanging) {
		return (
			<View style={{
				width: '100%',
				height: '100vh',
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				flexDirection: 'column',
				gap: '20px'
			}}>
				<Spin size="large" tip="토큰 교체 중...">
					<Alert
						message="로그인 정보를 업데이트하고 있습니다"
						description="잠시만 기다려주세요..."
						type="info"
						style={{ minWidth: '300px' }}
					/>
				</Spin>
			</View>
		)
	}

	// 토큰 교체 완료 또는 필요 없음 → 정상 렌더링
	return <>{children}</>
}

export default TokenExchangeGuard
